import { Empty } from '@/components/common/empty';
import { Debug } from '@/components/common/output/debug';
import { useApiWithAuth } from '@/hooks/api';
import type {
  PeopleAccess,
  PeopleAccessAction,
  Effect,
  PeopleRoleKey,
} from '@/store/access/access';
import { PeopleAccessEntries, peopleRolesList } from '@/store/access/access';
import { updatePolicy } from '@/store/access/actions';
import type { SharedSelection } from '@heroui/react';
import {
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  Spacer,
} from '@heroui/react';
import { useEffect, useMemo, useState } from 'react';
import { useAccessPolicy } from '../people/hooks';
import { useAccessStore } from '@/store/access';

export function Access() {
  const { api, auth } = useApiWithAuth();
  const store = useAccessStore();
  const { loaded, policy } = useAccessPolicy();
  const roles = useMemo(() => {
    return peopleRolesList();
  }, []);
  const access = useMemo(() => {
    return Object.entries(PeopleAccessEntries);
  }, []);
  const effects = useMemo(() => {
    return [
      { key: '', label: 'None' },
      { key: 'allow', label: 'Allow' },
      { key: 'deny', label: 'Deny' },
    ];
  }, []);
  const [roleRules, setRoleRules] = useState({} as PeopleAccess);
  const updateRules = (key: PeopleAccessAction, value: SharedSelection) => {
    const effect = (value?.currentKey || '') as Effect;
    const newRoleRules: PeopleAccess = { ...roleRules, [key]: effect };
    setRoleRules(newRoleRules);
    setAllRules({ ...allRules, [selectedRole]: newRoleRules });
  };

  const [selectedRole, setSelectedRole] = useState<PeopleRoleKey | ''>('');
  const [allRules, setAllRules] = useState(
    {} as Record<PeopleRoleKey, PeopleAccess>,
  );
  useEffect(() => {
    if (loaded && policy) {
      setAllRules(policy);
    }
  }, [loaded, policy]);
  const updateSelectedRole = (role: SharedSelection) => {
    const newSelectedRole = (role?.currentKey || '') as PeopleRoleKey;
    setSelectedRole(newSelectedRole);
    setRoleRules(allRules[newSelectedRole] || {});
  };

  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await updatePolicy(store, { api, auth, data: allRules });
    setSaving(false);
  };

  const borderLeftColor = (key: string) => {
    if (roleRules[key] === 'allow') {
      return 'border-l-success-500';
    } else if (roleRules[key] === 'deny') {
      return 'border-l-danger-500';
    }

    return 'border-l-default-200';
  };

  return (
    <div className='mx-2 sm:mx-0'>
      <div className='flex flex-row items-center justify-start'>
        <Select
          items={roles}
          className='sm:basis-6/12'
          label='Role'
          selectedKeys={[selectedRole]}
          onSelectionChange={updateSelectedRole}
        >
          {(role) => <SelectItem key={role.key}>{role.label}</SelectItem>}
        </Select>
        <Spacer className='grow' />
        <Button
          variant='solid'
          size='sm'
          color='primary'
          onPress={save}
          isLoading={saving}
        >
          Save
        </Button>
      </div>
      <Card
        className='mt-4 border-1 border-gray-200 px-1 py-2 sm:px-3'
        shadow='none'
      >
        {!selectedRole ? (
          <Empty description='Select a role to view or edit access' />
        ) : (
          <CardBody className='grid grid-cols-1 gap-2 md:grid-cols-2'>
            <div className='flex basis-6/12 flex-row items-center text-small font-medium'>
              <div className='md:4/12 shrink-0 basis-6/12 border-l-5 border-l-transparent pl-2 text-sm sm:basis-5/12'>
                Action
              </div>
              <div className='ml-2 shrink basis-6/12'>Effect</div>
            </div>
            <div className='hidden basis-6/12 flex-row items-center text-small font-medium md:flex'>
              <div className='md:4/12 shrink-0 basis-6/12 border-l-5 border-l-transparent pl-2 text-sm sm:basis-5/12'>
                Action
              </div>
              <div className='ml-2 basis-6/12'>Effect</div>
            </div>
            {access.map(([key, label]) => (
              <>
                <div
                  key={key}
                  className={`flex basis-6/12 flex-row items-center border-l-5 ${borderLeftColor(key)} pl-2`}
                >
                  <div className='md:4/12 shrink-0 basis-6/12 text-sm sm:basis-5/12'>
                    {label}
                  </div>
                  <div className='shrink basis-6/12'>
                    <Select
                      items={effects}
                      variant='flat'
                      size='sm'
                      selectedKeys={[roleRules[key]]}
                      onSelectionChange={(effect) =>
                        updateRules(key as PeopleAccessAction, effect)
                      }
                      renderValue={(items) =>
                        items.map((value) => (
                          <div key={value.key}>
                            {value.key ? value.textValue : ''}
                          </div>
                        ))
                      }
                    >
                      {(item) => (
                        <SelectItem key={item.key}>{item.label}</SelectItem>
                      )}
                    </Select>
                  </div>
                </div>
              </>
            ))}
          </CardBody>
        )}
      </Card>
      <Debug value={{ selectedRole, allRules }} />
    </div>
  );
}
